import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PublicNavBar from './PublicNavBar';
import httpClient from '../services/httpClient';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if email is enabled
    const checkEmailStatus = async () => {
      try {
        const response = await httpClient.get('/v1/auth/registration-info');
        setEmailEnabled(response.data.emailEnabled || false);
      } catch (err) {
        console.error('Failed to check email status:', err);
        setEmailEnabled(false);
      }
    };
    checkEmailStatus();
  }, []);

  if (emailEnabled === null) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <PublicNavBar />
        <Container component="main" maxWidth="xs">
          <Box
            sx={{
              marginTop: 12,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  if (!emailEnabled) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <PublicNavBar />
        <Container component="main" maxWidth="xs">
          <Box
            sx={{
              marginTop: 12,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
              <Typography component="h1" variant="h4" align="center" gutterBottom>
                Forgot Password
              </Typography>
              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                Password reset is currently unavailable. Email has not been configured.
                Please contact your administrator.
              </Alert>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate('/login')}
                sx={{ mt: 2 }}
              >
                Back to Login
              </Button>
            </Paper>
          </Box>
        </Container>
      </Box>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await httpClient.post('/v1/auth/forgot-password', { email });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PublicNavBar />
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 12,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography component="h1" variant="h4" align="center" gutterBottom>
              Forgot Password
            </Typography>
            <Typography variant="subtitle1" align="center" color="textSecondary" gutterBottom>
              Enter your email address and we'll send you a link to reset your password
            </Typography>

            {success ? (
              <Box sx={{ mt: 3 }}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  If an account exists with this email, a password reset link has been sent.
                  Please check your email inbox and spam folder.
                </Alert>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate('/login')}
                  sx={{ mt: 2 }}
                >
                  Back to Login
                </Button>
              </Box>
            ) : (
              <>
                {error && (
                  <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
                  </Button>

                  <Box textAlign="center" mt={2}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigate('/login')}
                      type="button"
                      sx={{ textDecoration: 'none' }}
                    >
                      Back to Login
                    </Link>
                  </Box>
                </Box>
              </>
            )}
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPassword;

